import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiProvider";
import { queryKeys } from "../../api/queries";
import type { Connection, ConnectionStatus } from "../../api/types";
import type { ConnectionTopology } from "../../components/StatePill";

/** Mirrors `proto.DefaultGatewayPort` (`proto/connect.go`) — the default TCP
 * port Kelpie's east-west workload gateway binds (ADR-0029 §2). Operator
 * guidance (the firewall hint in `ConnectionsSection`, and
 * `docs/install.md`) cites this number; if an install overrides it via
 * `-gateway-addr`/`COMEBYE_GATEWAY_ADDR` the hint is still directionally
 * correct but not exact — there is no API that reports the configured port
 * back to this client today. */
export const DEFAULT_GATEWAY_PORT = 4433;

/**
 * Node ids currently hosting each of `serviceIds`, fanned out from
 * `listPlacements` one call per service — the same client-composition
 * approach `useRouteRows` (ADR-0026/0027) uses for "which nodes serve this
 * service". Bounded the same way (`limit: 100` per service): an accepted
 * pre-alpha N+1, not new to this hook.
 */
export function useNodeIdsByService(
  projectId: string,
  serviceIds: string[],
): { nodeIdsByService: Map<string, string[]>; isPending: boolean; isError: boolean } {
  const client = useApiClient();

  const placementQueries = useQueries({
    queries: serviceIds.map((serviceId) => ({
      queryKey: queryKeys.placements(projectId, serviceId),
      queryFn: () => client.listPlacements({ projectId, serviceId, limit: 100 }),
      enabled: Boolean(projectId) && Boolean(serviceId),
    })),
  });

  const nodeIdsByService = useMemo(() => {
    const map = new Map<string, string[]>();
    serviceIds.forEach((serviceId, i) => {
      const items = placementQueries[i]?.data?.items ?? [];
      map.set(serviceId, items.map((p) => p.nodeId));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceIds, placementQueries.map((q) => q.dataUpdatedAt).join("|")]);

  return {
    nodeIdsByService,
    isPending: placementQueries.some((q) => q.isPending),
    isError: placementQueries.some((q) => q.isError),
  };
}

/**
 * Whether a connection's two endpoints currently share a node
 * (ADR-0029 §2: "local wins" — sharing even one node makes this a
 * same-node connection, so any overlap at all means same-node). Client-side
 * and always real, regardless of whether live reachability is available.
 */
export function computeTopology(fromNodeIds: string[], toNodeIds: string[]): ConnectionTopology {
  if (fromNodeIds.length === 0 || toNodeIds.length === 0) return "unplaced";
  const toSet = new Set(toNodeIds);
  const sharesNode = fromNodeIds.some((id) => toSet.has(id));
  return sharesNode ? "same-node" : "cross-node";
}

/**
 * Per-connection derived state for the cross-node status view (ADR-0029
 * P3-6): `topology` from `computeTopology` above (always real); `status`
 * is this connection's entry from the P3-5 batch reachability call (see
 * `ConnectionStatus`'s doc comment in types.ts) — `statusIsError` covers
 * both "the batch endpoint is unavailable" and any other transport
 * failure, and callers must render that the same as
 * `status.reachable === "unknown"`, never as a page-level error.
 */
export interface ConnectionRowMeta {
  topology: ConnectionTopology;
  fromNodeIds: string[];
  toNodeIds: string[];
  status: ConnectionStatus | undefined;
  statusPending: boolean;
  statusIsError: boolean;
}

/**
 * Folds `nodeIdsByService` (from `useNodeIdsByService`) and ONE batched
 * reachability call for the whole project (ADR-0029 P3-5:
 * `GET .../projects/{id}/connections/status` returns every connection's
 * status in one array, not per-connection) into a lookup keyed by
 * connection id. The batch call is fetched with `retry: false` and a
 * swallowed error — a failure degrades EVERY connection's pill to
 * "Unknown", never the page (mirrors `useRouteRows`'s `placementsDegraded`
 * posture for a secondary, non-blocking resolution).
 */
export function useConnectionStatusByConnection(
  projectId: string,
  connections: Connection[],
  nodeIdsByService: Map<string, string[]>,
): Map<string, ConnectionRowMeta> {
  const client = useApiClient();

  const statusesQuery = useQuery<ConnectionStatus[]>({
    queryKey: queryKeys.connectionStatuses(projectId),
    queryFn: () => client.getConnectionStatuses({ projectId }),
    enabled: Boolean(projectId),
    retry: false,
  });

  return useMemo(() => {
    const statusByConnectionId = new Map((statusesQuery.data ?? []).map((s) => [s.connectionId, s]));
    const map = new Map<string, ConnectionRowMeta>();
    connections.forEach((connection) => {
      const fromNodeIds = nodeIdsByService.get(connection.fromService) ?? [];
      const toNodeIds = nodeIdsByService.get(connection.toService) ?? [];
      map.set(connection.id, {
        topology: computeTopology(fromNodeIds, toNodeIds),
        fromNodeIds,
        toNodeIds,
        status: statusByConnectionId.get(connection.id),
        statusPending: statusesQuery.isPending,
        statusIsError: statusesQuery.isError,
      });
    });
    return map;
  }, [connections, nodeIdsByService, statusesQuery.data, statusesQuery.isPending, statusesQuery.isError]);
}
